import React, { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAdminUi } from "@/context/AdminUiContext";

const ACTION_TONES = {
  viewed_profile:    "bg-slate-500/15 text-slate-300 border-slate-400/30",
  viewed_booking:    "bg-slate-500/15 text-slate-300 border-slate-400/30",
  block_user:        "bg-amber-500/15 text-amber-200 border-amber-400/40",
  unblock_user:      "bg-teal-500/15 text-teal-200 border-teal-400/30",
  deleted_user:      "bg-red-500/15 text-red-300 border-red-400/40",
  added_admin_note:  "bg-blue-500/15 text-blue-200 border-blue-400/40",
  reset_demo_data:   "bg-red-500/15 text-red-300 border-red-400/40",
};

export default function AdminAuditLog() {
  const { openUser, openBooking } = useAdminUi();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/audit-log?limit=500");
        setRows(data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load audit log");
      } finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 text-slate-100" data-testid="admin-audit-page">
      <div className="mb-4">
        <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-teal-300">Admin · Audit Log</div>
        <h1 className="font-display font-extrabold text-2xl mt-1">Sensitive-action audit trail</h1>
        <p className="text-sm text-slate-400 mt-1">Every profile view, block/unblock, delete, and admin note is recorded here with actor and timestamp.</p>
      </div>
      <Card className="bg-white/[0.03] border-white/10">
        <CardContent className="p-0 overflow-x-auto">
          {loading ? (
            <div className="p-6 text-slate-400">Loading…</div>
          ) : (
            <table className="w-full text-sm min-w-[860px]">
              <thead className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-400 border-b border-white/10">
                <tr>
                  <th className="text-left p-3">When</th>
                  <th className="text-left p-3">Admin</th>
                  <th className="text-left p-3">Action</th>
                  <th className="text-left p-3">Target</th>
                  <th className="text-left p-3">Detail</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} data-testid={`audit-row-${i}`} className="border-t border-white/5 hover:bg-white/[0.02]">
                    <td className="p-3 text-xs text-slate-300 font-mono">{r.at ? new Date(r.at).toLocaleString() : "—"}</td>
                    <td className="p-3 text-xs text-slate-200">{r.actor_email || r.actor_id?.slice(0, 8)}</td>
                    <td className="p-3">
                      <span className={`inline-block px-2 py-0.5 rounded-full border font-mono text-[10px] uppercase tracking-[0.14em] ${ACTION_TONES[r.action] || "bg-slate-500/15 text-slate-200 border-slate-400/30"}`}>
                        {r.action?.replace(/_/g, " ") || "—"}
                      </span>
                    </td>
                    <td className="p-3 text-xs">
                      {r.target_user_id && (
                        <button onClick={() => openUser(r.target_user_id)} data-testid={`audit-open-user-${i}`} className="text-teal-300 hover:underline font-mono text-[11px]">
                          user {r.target_user_id.slice(0, 8)}
                        </button>
                      )}
                      {r.target_job_id && (
                        <button onClick={() => openBooking(r.target_job_id)} data-testid={`audit-open-booking-${i}`} className="ml-2 text-teal-300 hover:underline font-mono text-[11px]">
                          booking {r.target_job_id.slice(0, 8)}
                        </button>
                      )}
                      {!r.target_user_id && !r.target_job_id && <span className="text-slate-500">—</span>}
                    </td>
                    <td className="p-3 text-xs text-slate-400 max-w-md truncate" title={JSON.stringify(r.detail || {})}>
                      {r.detail ? JSON.stringify(r.detail).slice(0, 140) : "—"}
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr><td colSpan={5} className="p-10 text-center text-slate-500">No audit entries yet.</td></tr>
                )}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
